import { useCallback, useEffect, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import { Fab, Tooltip } from "@mui/material";
import L from "leaflet";
import { CableColor, CableType } from "../enums/enums";
import CableFormDialog, { CableFormValues } from "./CableFormDialog";
import CableIcon from "@mui/icons-material/Cable";

type Props = {
  onCreated: (payload: {
    points: [number, number][];
    type: CableType;
    color: CableColor;
  }) => Promise<void>;
};

export default function CableCreateButton({ onCreated }: Props) {
  const map = useMap() as any;
  const [open, setOpen] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const pendingLayerRef = useRef<L.Polyline | null>(null);
  const pendingPointsRef = useRef<[number, number][]>([]);

  const resetPending = () => {
    if (pendingLayerRef.current) {
      map.removeLayer(pendingLayerRef.current);
      pendingLayerRef.current = null;
    }
    pendingPointsRef.current = [];
  };

  const disableDraw = useCallback(() => {
    try {
      if (map?.pm?.globalDrawModeEnabled?.()) {
        map.pm.disableDraw();
      } else if (map?.pm?.Draw?.Line?.enabled()) {
        map.pm.disableDraw("Line");
      }
    } catch {}
    setIsDrawing(false);
  }, [map]);

  const cancelAll = useCallback(() => {
    disableDraw();
    resetPending();
    setOpen(false);
  }, [disableDraw]);

  const startDraw = () => {
    if (!map?.pm) return;
    map.pm.enableDraw("Line", { snappable: true, snapDistance: 20 });
  };

  useEffect(() => {
    if (!map) return;

    const handleCreate = (e: any) => {
      if (e.layer?.toGeoJSON?.().geometry?.type !== "LineString") return;

      const coords = (
        e.layer.toGeoJSON().geometry.coordinates as [number, number][]
      ).map(([lng, lat]) => [lat, lng] as [number, number]);

      pendingLayerRef.current = e.layer as L.Polyline;
      pendingPointsRef.current = coords;

      map.pm.disableDraw("Line");
      setOpen(true);
    };

    const handleDrawStart = () => setIsDrawing(true);
    const handleDrawEnd = () => setIsDrawing(false);

    map.on("pm:create", handleCreate);
    map.on("pm:drawstart", handleDrawStart);
    map.on("pm:drawend", handleDrawEnd);

    return () => {
      map.off("pm:create", handleCreate);
      map.off("pm:drawstart", handleDrawStart);
      map.off("pm:drawend", handleDrawEnd);
    };
  }, [map, disableDraw]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        cancelAll();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cancelAll]);

  const handleClose = () => {
    setOpen(false);
    if (pendingLayerRef.current) {
      map.removeLayer(pendingLayerRef.current);
      pendingLayerRef.current = null;
      pendingPointsRef.current = [];
    }
  };

  const handleSubmit = async (values: CableFormValues) => {
    const pts = pendingPointsRef.current;
    await onCreated({ points: pts, type: values.type, color: values.color });
    if (pendingLayerRef.current) {
      map.removeLayer(pendingLayerRef.current);
      pendingLayerRef.current = null;
    }
    pendingPointsRef.current = [];
    setOpen(false);
  };

  return (
    <>
      <Tooltip title={"Draw Cable"}>
        <Fab
          color="primary"
          onClick={startDraw}
          sx={{
            position: "fixed",
            right: "calc(env(safe-area-inset-right, 0px) + 16px)",
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 88px)",
            zIndex: 5000,
          }}
        >
          <CableIcon />
        </Fab>
      </Tooltip>

      <CableFormDialog
        open={open}
        onClose={handleClose}
        onSubmit={handleSubmit}
      />
    </>
  );
}
